import { useCallback } from 'react';

const DEFAULT_TTL = 10 * 60 * 1000; // 10 min

export function useLocalStorageWithTtl<T>(key: string, ttlMs = DEFAULT_TTL) {
  const setValue = useCallback(
    (value: T) => {
      const expiresAt = Date.now() + ttlMs;
      localStorage.setItem(key, JSON.stringify({ value, expiresAt }));
    },
    [key, ttlMs],
  );

  const getValue = useCallback((): T | null => {
    const item = localStorage.getItem(key);
    if (!item) return null;

    try {
      const { value, expiresAt } = JSON.parse(item) as {
        value: T;
        expiresAt: number;
      };
      if (Date.now() > expiresAt) {
        localStorage.removeItem(key);
        return null;
      }
      return value;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  }, [key]);

  const removeValue = useCallback(() => {
    localStorage.removeItem(key);
  }, [key]);

  return { setValue, getValue, removeValue };
}
